import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Pencil, Trash2, Save, ExternalLink, Settings as Cog, ChevronRight, Database, SlidersHorizontal, Clock3 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, Spinner, EmptyState } from "../components/ui";

const TYPE_BADGE = { MASTER: ["List", "info"], CONFIG: ["Config", "ok"], LINK: ["Module", "warn"], SOON: ["Roadmap", "neutral"] };

function Field({ f, value, onChange }) {
  const common = { "data-testid": `field-${f.name}`, value: value ?? "", onChange: (e) => onChange(f.type === "toggle" ? e.target.checked : e.target.value), className: "input" };
  if (f.type === "toggle")
    return <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300"><input type="checkbox" data-testid={`field-${f.name}`} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-accent" /> {f.label}</label>;
  if (f.type === "textarea") return <div><label className="lbl mb-1 block">{f.label}</label><textarea {...common} className="input min-h-[80px]" /></div>;
  if (f.type === "select") return <div><label className="lbl mb-1 block">{f.label}</label><select {...common}>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
  return <div><label className="lbl mb-1 block">{f.label}</label><input type={f.type === "number" ? "number" : "text"} {...common} /></div>;
}

function MasterPanel({ setting }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null); // {id?, ...values}
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/settings/master/${setting.key}`).then(({ data }) => setData(data));
  useEffect(() => { setData(null); load(); }, [setting.key]);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.put(`/settings/master/${setting.key}/${edit.id}`, edit);
      else await api.post(`/settings/master/${setting.key}`, edit);
      setEdit(null); load();
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (window.confirm("Delete this item?")) { await api.delete(`/settings/master/${setting.key}/${id}`); load(); } };

  if (!data) return <Loading />;
  const f = data.setting;
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div><h2 className="font-head text-xl font-bold text-slate-900 dark:text-white">{f.label}</h2><p className="text-sm text-slate-500 dark:text-slate-400">{f.desc}</p></div>
        <button data-testid="master-add" onClick={() => setEdit(Object.fromEntries(f.fields.map((x) => [x.name, x.type === "toggle" ? false : ""])))} className="btn-accent flex items-center gap-1.5"><Plus size={16} /> Add</button>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="master-table">
            <thead><tr>{f.fields.map((x) => <th key={x.name} className="th">{x.label}</th>)}<th className="th text-right">Actions</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.items.length === 0 ? <tr><td className="td text-slate-400" colSpan={f.fields.length + 1}>No entries yet — click Add.</td></tr> :
                data.items.map((it) => (
                  <tr key={it.id} data-testid={`master-row-${it.id}`}>
                    {f.fields.map((x) => <td key={x.name} className="td">{x.type === "toggle" ? (it[x.name] ? "Yes" : "No") : (it[x.name] ?? "—")}</td>)}
                    <td className="td"><div className="flex items-center justify-end gap-2">
                      <button data-testid={`master-edit-${it.id}`} onClick={() => setEdit({ id: it.id, ...Object.fromEntries(f.fields.map((x) => [x.name, it[x.name]])) })} className="text-accent hover:underline"><Pencil size={14} /></button>
                      <button data-testid={`master-del-${it.id}`} onClick={() => del(it.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? `Edit ${f.label}` : `Add ${f.label}`}
        footer={<><button onClick={() => setEdit(null)} className="btn-outline">Cancel</button><button data-testid="master-save" disabled={busy} onClick={save} className="btn-accent">{busy ? <Spinner className="text-white" /> : "Save"}</button></>}>
        {edit && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{f.fields.map((x) => <Field key={x.name} f={x} value={edit[x.name]} onChange={(v) => setEdit((s) => ({ ...s, [x.name]: v }))} />)}</div>}
      </Modal>
    </div>
  );
}

function ConfigPanel({ setting }) {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setData(null); api.get(`/settings/config/${setting.key}`).then(({ data }) => { setData(data); setVals(data.values); }); }, [setting.key]);
  const save = async () => { setBusy(true); try { await api.put(`/settings/config/${setting.key}`, vals); setSaved(true); setTimeout(() => setSaved(false), 3000); } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  if (!data) return <Loading />;
  return (
    <div>
      <div className="mb-4"><h2 className="font-head text-xl font-bold text-slate-900 dark:text-white">{data.setting.label}</h2><p className="text-sm text-slate-500 dark:text-slate-400">{data.setting.desc}</p></div>
      <Card className="max-w-2xl p-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {data.setting.schema.map((f) => <Field key={f.name} f={f} value={vals[f.name]} onChange={(v) => setVals((s) => ({ ...s, [f.name]: v }))} />)}
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button data-testid="config-save" disabled={busy} onClick={save} className="btn-accent flex items-center gap-1.5">{busy ? <Spinner className="text-white" /> : <><Save size={15} /> Save Changes</>}</button>
          {saved && <span data-testid="config-saved" className="text-sm font-medium text-emerald-600">Saved ✓</span>}
        </div>
      </Card>
    </div>
  );
}

export default function Settings() {
  const [reg, setReg] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/settings/registry").then(({ data }) => { setReg(data); }); }, []);
  useEffect(() => {
    if (reg && !active) {
      // default to first functional setting
      for (const a of reg.areas) for (const g of a.groups) for (const it of g.items) if (it.type === "MASTER" || it.type === "CONFIG") { setActive(it); return; }
    }
  }, [reg, active]);

  const filtered = useMemo(() => {
    if (!reg) return [];
    if (!q) return reg.areas;
    const ql = q.toLowerCase();
    return reg.areas.map((a) => ({ ...a, groups: a.groups.map((g) => ({ ...g, items: g.items.filter((it) => it.label.toLowerCase().includes(ql) || it.desc.toLowerCase().includes(ql)) })).filter((g) => g.items.length) })).filter((a) => a.groups.length);
  }, [reg, q]);

  if (!reg) return <Loading />;

  return (
    <div>
      <PageHeader title="Settings" subtitle={`${reg.totalSettings} configuration items across ${reg.areas.length} areas · registry-driven`} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        {/* left registry nav */}
        <Card className="h-[calc(100vh-220px)] overflow-hidden p-0">
          <div className="border-b border-slate-100 p-3 dark:border-slate-800">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="settings-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search settings…" className="input pl-9" />
            </div>
          </div>
          <div className="h-[calc(100%-64px)] overflow-y-auto p-2" data-testid="settings-nav">
            {filtered.map((a) => (
              <div key={a.area} className="mb-2">
                <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{a.area}</div>
                {a.groups.map((g) => (
                  <div key={g.group} className="mb-1">
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400">{g.group}</div>
                    {g.items.map((it) => (
                      <button key={it.key} data-testid={`setting-${it.key}`} onClick={() => setActive(it)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm ${active?.key === it.key ? "bg-accent/10 font-semibold text-accent" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
                        <span className="truncate">{it.label}</span>
                        {it.type === "MASTER" ? <Database size={12} className="shrink-0 text-slate-300" /> : it.type === "CONFIG" ? <SlidersHorizontal size={12} className="shrink-0 text-slate-300" /> : it.type === "LINK" ? <ExternalLink size={12} className="shrink-0 text-slate-300" /> : <Clock3 size={12} className="shrink-0 text-slate-300" />}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>

        {/* main panel */}
        <div data-testid="settings-panel">
          {!active ? <EmptyState icon={Cog} title="Select a setting" /> :
            active.type === "MASTER" ? <MasterPanel setting={active} /> :
            active.type === "CONFIG" ? <ConfigPanel setting={active} /> :
            active.type === "LINK" ? (
              <Card className="p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600"><ExternalLink size={24} /></div>
                <h2 className="font-head text-xl font-bold text-slate-900 dark:text-white">{active.label}</h2>
                <p className="mx-auto mb-5 max-w-md text-sm text-slate-500 dark:text-slate-400">{active.desc}</p>
                <button data-testid="link-open" onClick={() => navigate(active.to)} className="btn-accent">Open module</button>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-500/10 text-slate-500"><Clock3 size={24} /></div>
                <div className="mb-2 flex items-center justify-center gap-2"><h2 className="font-head text-xl font-bold text-slate-900 dark:text-white">{active.label}</h2><Badge tone="neutral">Roadmap</Badge></div>
                <p className="mx-auto max-w-md text-sm text-slate-500 dark:text-slate-400">{active.desc}</p>
                <p className="mt-4 text-xs text-slate-400">Catalogued from the SMIFS discovery. This configuration screen is scheduled for a later phase.</p>
              </Card>
            )}
        </div>
      </div>
    </div>
  );
}
